import useNotification from "apps/hooks/useNotification";
import { getActiveUser, getUserInfo } from "features/auth/store/slice";
import { FC, Suspense, lazy, memo } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { ADMIN_ROLE, SUPER_ADMIN_ROLE } from "utils/constants";
import Loading from "views/components/Bases/Loading";
import Header from "views/components/Layouts/Header";
import styles from "./styles.module.scss";
import SecurityMonitoring from "views/pages/security-monitoring";

const SecurityCopilotLayout = lazy(() => import("../pages/security-ai-chat"));
const APICopilotLayout = lazy(() => import("../pages/api-ai-chat"));
const AuditCopilotLayout = lazy(() => import("../pages/audit-ai-chat"));
const DocumentManagementLayout = lazy(
  () => import("../pages/document-management")
);
const UserPermissionLayout = lazy(() => import("../pages/user-permission"));
const SystemConfigLayout = lazy(() => import("../pages/system-config"));
const NoPermissionLayout = lazy(() => import("../pages/permission-page"));
const NoAccessLayout = lazy(() => import("../pages/access-page"));
const HistoryChatLayout = lazy(() => import("../pages/history-management"));
const ConfigRuleLayout = lazy(() => import("../pages/config-rules"));
const CrawlingURLLayout = lazy(() => import("../pages/crawling-url-page"));
const HomePageLayout = lazy(() => import("../pages/home-page"));
const NotFound = lazy(() => import("../pages/page-not-found"));

const MainLayout = () => (
  <>
    <Header />
    <div className={styles.content}>
      <Outlet />
    </div>
  </>
);

const Layout: FC = () => {
  const active_user = useSelector(getActiveUser);
  const user_info = useSelector(getUserInfo);

  useNotification();

  if (!active_user) {
    return <NoPermissionLayout />;
  }

  return (
    <div className={styles.container}>
      {/* <Header /> */}
      {/* <div className={styles.content}> */}
      {/* <div className={styles.layout_content}> */}
      <Routes>
        <Route path="/" element={<Outlet />} />
        <Route index element={<Navigate to="/home" replace />} />
        <Route
          path="/home"
          element={
            <Suspense fallback={<Loading />}>
              <HomePageLayout />
            </Suspense>
          }
        />
        <Route element={<MainLayout />}>
          <Route
            path="/security-copilot"
            element={
              <Suspense fallback={<Loading />}>
                <SecurityCopilotLayout />
              </Suspense>
            }
          />
          <Route
            path="/api-copilot"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.webosapi ? (
                  <APICopilotLayout />
                ) : (
                  <NoAccessLayout />
                )}
              </Suspense>
            }
          />
          <Route
            path="/audit-copilot"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.audit ? <AuditCopilotLayout /> : <NoAccessLayout />}
              </Suspense>
            }
          />
          <Route
            path="/document-management"
            element={
              <Suspense fallback={<Loading />}>
                <DocumentManagementLayout />
              </Suspense>
            }
          />
          <Route
            path="/user-management"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.role === SUPER_ADMIN_ROLE ||
                user_info?.role === ADMIN_ROLE ? (
                  <UserPermissionLayout />
                ) : (
                  <NoPermissionLayout />
                )}
              </Suspense>
            }
          />
          <Route
            path="/config-prompt"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.role === SUPER_ADMIN_ROLE ? (
                  <SystemConfigLayout />
                ) : (
                  <NoPermissionLayout />
                )}
              </Suspense>
            }
          />
          <Route
            path="/config-rule"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.role === SUPER_ADMIN_ROLE ? (
                  <ConfigRuleLayout />
                ) : (
                  <NoPermissionLayout />
                )}
              </Suspense>
            }
          />
          <Route
            path="/history-management"
            element={
              <Suspense fallback={<Loading />}>
                <HistoryChatLayout />
              </Suspense>
            }
          />
          <Route
            path="/crawling-management"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.role === SUPER_ADMIN_ROLE ? (
                  <CrawlingURLLayout />
                ) : (
                  <NoPermissionLayout />
                )}
              </Suspense>
            }
          />
          <Route
            path="/security-management"
            element={
              <Suspense fallback={<Loading />}>
                {user_info?.securityLog || user_info?.role === SUPER_ADMIN_ROLE ? (
                  <SecurityMonitoring />
                ) : (
                  <NoPermissionLayout />
                )}
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<Loading />}>
                <NotFound />
              </Suspense>
            }
          />
        </Route>
      </Routes>
      {/* </div> */}
      {/* </div> */}
    </div>
  );
};

export default memo(Layout);
